import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { message } from 'antd';

/**
 * Axios instance for the backoffice half of seiki-backend.
 *
 * Deliberately separate from seiki_web's client: this one authenticates as an
 * `Admin` (JWT minted with scope="backoffice") and sends none of the customer
 * app's tenant/workspace headers, which would be meaningless here.
 */

const ACCESS_TOKEN_KEY = 'seiki_backoffice_token';
const REFRESH_TOKEN_KEY = 'seiki_backoffice_refresh_token';

export const tokenStore = {
  getAccess: () => window.localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => window.localStorage.getItem(REFRESH_TOKEN_KEY),
  set(access: string, refresh?: string) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (refresh) window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear() {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/** `{ code, message, data }` — the shape every seiki-backend route returns. */
interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T;
}

const BASE_URL = '/api/v1/backoffice';

const service = axios.create({
  baseURL: BASE_URL,
  timeout: 50_000,
  headers: { 'Content-Type': 'application/json;charset=UTF-8' },
});

/** Interceptor-free client, so refreshing can never recurse into the 401 handler. */
const refreshClient = axios.create({ baseURL: BASE_URL, timeout: 50_000 });

let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

// One in-flight refresh shared by every request that fails at the same time, so
// a screen firing several calls triggers exactly one /auth/refresh round-trip.
let refreshPromise: Promise<string> | null = null;

function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  const refresh_token = tokenStore.getRefresh()?.replace(/^bearer\s+/i, '') ?? null;
  if (!refresh_token) return Promise.reject(new Error('no_refresh_token'));

  refreshPromise = refreshClient
    .post<ApiEnvelope<{ access_token: string; refresh_token?: string }>>(
      '/auth/refresh',
      { refresh_token },
    )
    .then((resp) => {
      const payload = resp.data?.data;
      if (!payload?.access_token) throw new Error('refresh_failed');
      tokenStore.set(payload.access_token, payload.refresh_token);
      return payload.access_token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

function endSession(reason: string) {
  tokenStore.clear();
  message.error(reason, 5);
  onSessionExpired?.();
}

service.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    // Tokens are stored bare; the backoffice deps use OAuth2PasswordBearer,
    // which requires the "Bearer " prefix on the wire.
    config.headers.Authorization = token.match(/^bearer\s/i) ? token : `Bearer ${token}`;
  }
  return config;
});

service.interceptors.response.use(
  (response) => {
    // A 2xx can still carry a business-level failure code, matching seiki_web.
    const body = response.data as ApiEnvelope<unknown> | undefined;
    if (body && typeof body.code === 'number' && ![200, 201, 204].includes(body.code)) {
      if (!(response.config as RequestConfig).silent) message.error(body.message);
      return Promise.reject(new ApiError(body.message, body.code, response.status));
    }
    return response;
  },
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const status = error.response?.status;
    const serverMessage = error.response?.data?.message;
    const original = (error.config ?? {}) as RequestConfig;
    const isAuthCall = /\/auth\/(refresh|login|logout|google)$/.test(original.url ?? '');

    // 401 = access token expired. Try one silent refresh + replay; only a failed
    // refresh actually ends the session.
    if (status === 401 && !isAuthCall && !original._retry) {
      original._retry = true;
      try {
        const token = await refreshAccessToken();
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
        return service(original);
      } catch {
        endSession('Your session has expired. Please sign in again.');
        return Promise.reject(new ApiError('Session expired', 401, 401));
      }
    }

    // The backoffice API answers 403 for BOTH an unusable token and a refused
    // action (app/api/backoffice/deps.py). Only the former is a session
    // problem — logging out on a permission error would bounce a legitimately
    // signed-in admin straight back to a login screen that "works".
    if (status === 403 && !isAuthCall) {
      const isTokenProblem =
        !!serverMessage && /invalid authentication|inactive admin/i.test(serverMessage);
      if (isTokenProblem) {
        endSession(serverMessage);
      } else if (!original.silent) {
        message.error(serverMessage ?? 'You do not have permission to do that.', 5);
      }
      return Promise.reject(new ApiError(serverMessage ?? 'Forbidden', 403, 403));
    }

    // Pydantic field errors (body code 1001) are far more useful than the
    // generic "Validation error" wrapper the envelope carries.
    const validationDetail = extractValidationDetail(error.response?.data);
    const text = validationDetail ?? serverMessage ?? error.message ?? 'Request failed';
    if (!original.silent) message.error(text, 5);
    return Promise.reject(new ApiError(text, error.response?.data?.code, status));
  },
);

function extractValidationDetail(body: ApiEnvelope<unknown> | undefined): string | null {
  if (!body || body.code !== 1001 || !Array.isArray(body.data) || !body.data.length) {
    return null;
  }
  const parts = (body.data as Array<{ loc?: unknown[]; msg?: string }>)
    .map((err) => {
      const msg = typeof err.msg === 'string' ? err.msg.replace(/^Value error,\s*/, '') : null;
      if (!msg) return null;
      const field = Array.isArray(err.loc)
        ? err.loc.filter((p) => p !== 'body').join('.')
        : '';
      return field ? `${field}: ${msg}` : msg;
    })
    .filter(Boolean);
  return parts.length ? parts.join('\n') : null;
}

export class ApiError extends Error {
  code?: number;
  status?: number;
  constructor(msg: string, code?: number, status?: number) {
    super(msg);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/** `silent` suppresses the automatic error toast when a caller renders its own. */
interface RequestConfig extends AxiosRequestConfig {
  silent?: boolean;
  _retry?: boolean;
}

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const response = await promise;
  return response.data.data;
}

export const http = {
  get: <T>(url: string, params?: unknown, config?: RequestConfig) =>
    unwrap<T>(service.get(url, { params, ...config })),
  post: <T>(url: string, body?: unknown, config?: RequestConfig) =>
    unwrap<T>(service.post(url, body, config)),
  put: <T>(url: string, body?: unknown, config?: RequestConfig) =>
    unwrap<T>(service.put(url, body, config)),
  del: <T>(url: string, config?: RequestConfig) => unwrap<T>(service.delete(url, config)),
};

export { service as axiosInstance };
